/**
 * Well-job steps (api#1055), mirroring fieldiq-api `src/lib/dh-jobs.ts`. The
 * API is the enforcement; this decides which button a screen offers.
 *
 *   scheduled → mobilizing → on_location → released → completed
 *   cancel only from scheduled or mobilizing (a reason, no time)
 *
 * Each forward step stamps one instant. Completing stamps `demobilized_at`,
 * which is why the last button reads "Demobilized" rather than "Complete".
 */
import type { DhJobStampColumn, DhJobStatus, DhJobTransitionTarget } from '../types/downhole';

export interface DhJobStep {
  to: Exclude<DhJobTransitionTarget, 'cancelled'>;
  /** The button. */
  label: string;
  /** The instant this step stamps. */
  stamp: DhJobStampColumn;
}

/** The one forward step from each status. Absent: the job is at its end. */
export const DH_JOB_NEXT_STEP: Partial<Record<DhJobStatus, DhJobStep>> = {
  scheduled: { to: 'mobilizing', label: 'Mobilize', stamp: 'mobilized_at' },
  mobilizing: { to: 'on_location', label: 'Arrived on location', stamp: 'on_location_at' },
  on_location: { to: 'released', label: 'Released', stamp: 'released_at' },
  released: { to: 'completed', label: 'Demobilized', stamp: 'demobilized_at' },
};

/** The Timeline rows, in order. */
export const DH_JOB_STAMP_LABEL: Record<DhJobStampColumn, string> = {
  mobilized_at: 'Mobilized',
  on_location_at: 'On location',
  released_at: 'Released',
  demobilized_at: 'Demobilized',
};

export const DH_JOB_CLOSED: readonly string[] = ['completed', 'cancelled'];

export function nextJobStep(status: string | null | undefined): DhJobStep | null {
  return (status && DH_JOB_NEXT_STEP[status as DhJobStatus]) || null;
}

/** A closed job takes notes and nothing else. */
export function isJobClosed(status: string | null | undefined): boolean {
  return !!status && DH_JOB_CLOSED.includes(status);
}

export function canCancelJob(status: string | null | undefined): boolean {
  return status === 'scheduled' || status === 'mobilizing';
}

/** DELETE takes a scheduled or cancelled job with no assemblies on it. */
export function canDeleteJob(status: string | null | undefined, assemblyCount: number | null | undefined): boolean {
  return (status === 'scheduled' || status === 'cancelled') && assemblyCount === 0;
}
